import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  IconButton,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { FiSend, FiZap } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import { WorkspaceAPI } from '../../services/api';
import { useWorkspaceAssistant } from './WorkspaceAssistantContext';

// Cards show the numbers straight from /workspace/status; the model only explains them.
const STATUS_CARDS = [
  { key: 'calls_today', zh: '今日通话', en: 'Calls today' },
  { key: 'callbacks_pending', zh: '待处理回电', en: 'Callbacks waiting', warn: true },
  { key: 'sms_failed_24h', zh: '24h 短信失败', en: 'SMS failed (24h)', warn: true },
  { key: 'chats_escalated_24h', zh: '24h 聊天转人工', en: 'Chats to staff (24h)', warn: true },
  { key: 'calls_7d', zh: '7 天通话', en: 'Calls (7 days)' },
  { key: 'chats_24h', zh: '24h 网页聊天', en: 'Website chats (24h)' },
];

const QUICK_PROMPTS = {
  zh: ['这个页面怎么用？', '今天的情况怎么样？', '有什么需要我处理的？'],
  en: ['How do I use this page?', 'How are things today?', 'What needs my attention?'],
};

const errorText = (error, zh) => {
  const status = error?.response?.status;
  if (status === 429) return zh ? '提问太频繁了，请稍后再试（每小时 30 次）。' : 'Too many questions. Try again later (30 per hour).';
  return zh ? '助手暂时不可用，请稍后再试。' : 'The assistant is unavailable right now. Please try again later.';
};

const AssistantPanel = ({ ctx, zh, pageTitle }) => {
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    Promise.resolve().then(() => WorkspaceAPI.status()).then((data) => setStatus(data?.status || null)).catch(() => {});
  }, [isOpen]);

  useEffect(() => { endRef.current?.scrollIntoView?.({ block: 'end' }); }, [messages, busy]);

  const ask = async (text) => {
    const message = String(text || '').trim();
    if (!message || busy) return;
    const history = messages.filter((item) => !item.error).map(({ role, text: body }) => ({ role, text: body }));
    setMessages((current) => [...current, { role: 'user', text: message }]);
    setDraft('');
    setBusy(true);
    try {
      const data = await WorkspaceAPI.ask({
        message,
        language: zh ? 'zh' : 'en',
        page: { path: location.pathname, title: pageTitle || '', help: ctx.pageHelp || '' },
        history,
      });
      if (data?.status) setStatus(data.status);
      setMessages((current) => [...current, { role: 'assistant', text: data?.reply || '' }]);
    } catch (error) {
      setMessages((current) => [...current, { role: 'assistant', text: errorText(error, zh), error: true }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        position="fixed" bottom={{ base: 4, md: 8 }} right={{ base: 4, md: 6 }} zIndex="docked"
        colorScheme="blue" borderRadius="full" shadow="lg" leftIcon={<FiZap />} onClick={onOpen}
        size="md" data-testid="workspace-assistant-open"
      >
        {zh ? 'AI 助手' : 'Assistant'}
      </Button>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Text>{zh ? 'AI 助手' : 'Assistant'}</Text>
            <Text fontSize="xs" color="gray.500" fontWeight="normal">
              {zh ? `当前页面：${pageTitle || '—'} · 只读，不会替你操作` : `Page: ${pageTitle || '—'} · read-only, never acts for you`}
            </Text>
          </DrawerHeader>
          <DrawerBody>
            <SimpleGrid columns={3} spacing={2} mb={4} data-testid="workspace-status-cards">
              {STATUS_CARDS.map((card) => {
                const value = status?.[card.key];
                const alert = card.warn && Number(value) > 0;
                return (
                  <Box key={card.key} p={2} borderWidth="1px" borderRadius="md"
                    bg={alert ? 'orange.50' : 'gray.50'} borderColor={alert ? 'orange.200' : 'gray.100'}>
                    <Text fontSize="lg" fontWeight="bold" color={alert ? 'orange.600' : 'gray.800'}>
                      {value === null || value === undefined ? '—' : value}
                    </Text>
                    <Text fontSize="xs" color="gray.600" noOfLines={2}>{zh ? card.zh : card.en}</Text>
                  </Box>
                );
              })}
            </SimpleGrid>
            <VStack align="stretch" spacing={3}>
              {messages.length === 0 && (
                <Text fontSize="sm" color="gray.600">
                  {zh
                    ? '我可以讲解当前页面、总结当前状态和通知。数字都来自上面的卡片。'
                    : 'I can explain this page and summarise your status and notifications. Numbers come from the cards above.'}
                </Text>
              )}
              {messages.map((item, index) => (
                <Box key={index} alignSelf={item.role === 'user' ? 'flex-end' : 'flex-start'} maxW="90%"
                  bg={item.role === 'user' ? 'blue.500' : item.error ? 'red.50' : 'gray.100'}
                  color={item.role === 'user' ? 'white' : item.error ? 'red.700' : 'gray.800'}
                  px={3} py={2} borderRadius="lg" whiteSpace="pre-wrap" fontSize="sm">
                  {item.text}
                </Box>
              ))}
              {busy && <HStack color="gray.500" fontSize="sm"><Spinner size="xs" /><Text>{zh ? '思考中…' : 'Thinking…'}</Text></HStack>}
              <Box ref={endRef} />
            </VStack>
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px" flexDirection="column" alignItems="stretch" gap={2}>
            <HStack spacing={2} flexWrap="wrap">
              {QUICK_PROMPTS[zh ? 'zh' : 'en'].map((prompt) => (
                <Button key={prompt} size="xs" variant="outline" borderRadius="full" onClick={() => ask(prompt)} isDisabled={busy}>
                  {prompt}
                </Button>
              ))}
            </HStack>
            <HStack align="end">
              <Textarea value={draft} onChange={(event) => setDraft(event.target.value)} rows={2} resize="none"
                maxLength={1000} placeholder={zh ? '问点什么…' : 'Ask something…'}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                    event.preventDefault();
                    ask(draft);
                  }
                }} />
              <IconButton colorScheme="blue" icon={<FiSend />} aria-label={zh ? '发送' : 'Send'}
                onClick={() => ask(draft)} isLoading={busy} isDisabled={!draft.trim()} />
            </HStack>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};

const AssistantDrawer = ({ zh = true, pageTitle }) => {
  const ctx = useWorkspaceAssistant();
  return ctx?.enabled ? <AssistantPanel ctx={ctx} zh={zh} pageTitle={pageTitle} /> : null;
};

export default AssistantDrawer;
